import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useNotifications, Notification } from "@/hooks/useNotifications";

interface InviteResponseActionsProps {
  notification: Notification;
}

export function InviteResponseActions({
  notification,
}: InviteResponseActionsProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { markAsRead } = useNotifications();

  const handleInviteResponse = async (accept: boolean) => {
    if (!notification.data?.invite_id) {
      toast({
        title: "Error",
        description: "Invalid invitation data",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sponsor_artist_invites")
        .update({
          status: accept ? "accepted" : "rejected",
        })
        .eq("id", notification.data.invite_id);

      if (error) throw error;

      markAsRead(notification.id);

      if (accept && notification.data.track_id) {
        const { error: trackError } = await supabase
          .from("tracks")
          .update({
            is_sponsored: true,
            sponsor_id: notification.data?.sponsor_id,
          })
          .eq("id", notification.data.track_id);

        if (trackError) {
          console.error("Error updating track:", trackError);
        }
      }

      toast({
        title: accept ? "Invitation Accepted" : "Invitation Declined",
        description: accept
          ? "You have successfully accepted the sponsorship request"
          : "You have declined the sponsorship request",
      });

      queryClient.invalidateQueries({ queryKey: ["sponsorProfile"] });
      queryClient.invalidateQueries({ queryKey: ["sponsoredArtists"] });
    } catch (error: any) {
      console.error("Error processing invite:", error);
      toast({
        title: "Error",
        description: "Failed to process the invitation",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (notification.type !== "artist_invite" || !notification.data) {
    return null;
  }

  const { pitch } = notification.data;

  return (
    <div className="mt-3 bg-muted/20 rounded-md p-3 text-sm">
      <p className="font-medium mb-1">Artist Pitch:</p>
      <p className="text-muted-foreground text-xs italic whitespace-pre-wrap">
        {pitch || "No pitch provided"}
      </p>

      <div className="mt-3 flex space-x-2">
        <Button
          variant="default"
          size="sm"
          className="flex-1"
          onClick={() => handleInviteResponse(true)}
          disabled={isProcessing}
        >
          <Check className="mr-1 h-4 w-4" />
          Accept
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => handleInviteResponse(false)}
          disabled={isProcessing}
        >
          <X className="mr-1 h-4 w-4" />
          Decline
        </Button>
      </div>
    </div>
  );
}
